import { useTranslation } from "react-i18next";
import { colors, css, fonts } from "../../Styles";
import { CheckBox, CheckStyles, TooltipString } from "../BaseComponents";
import { CSSProperties, memo, useMemo } from "react";
import { AttentionLog } from "../../Services/Agrisys_GqlDataCaller/AttentionLogCalls/GQLCalls/getAttentionLogs";
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";
interface AttentionBar_Props {
  attention?: AttentionLog
  onCheck?: () => void;
  borderRadius?: CSSProperties;
  headerBar?: boolean
  attentionColor?: colors
  selectable?: boolean;
  isChecked?: boolean;
}
/**
 * PEN_BASIC
 * @param attention - A single attention log entry containing event details.
 * @param onCheck - Callback triggered when the checkbox is toggled (if `selectable` is `true`).
 * @param borderRadius - Optional custom border radius for the outer container (e.g., `{ borderRadius: "6px" }`).
 * @param headerBar - If `true`, displays column headers instead of actual data.
 * @param attentionColor - Optional color used for the left border to indicate severity or type (e.g., `colors.red`).
 * @param selectable - If `true`, shows a checkbox at the beginning of the row.
 * @param isChecked - Indicates whether the checkbox should appear as checked.
 * @returns A styled row element displaying either header labels or formatted attention data with tooltips.
 */
function ATTENTION_BAR({ attention, onCheck, borderRadius, headerBar=false, attentionColor, selectable=false, isChecked=false }: AttentionBar_Props) {
  const { t } = useTranslation();

  // Formatted timestamp
  const formattedTime = useMemo(() => {
    if (!attention?.timestamp) return "";
    return new LocaleFarmDate(attention.timestamp*1000).toLocaleFarmString();
  }, [attention?.timestamp]);

  // Translations
  const translations = useMemo(() => ({
    locationName: t("systemControls.general.location_name", { defaultValue: "Location Name" }),
    fk_lan_controllers: t("systemControls.general.fk_lan_controllers", { defaultValue: "FK Lan Controllers" }),
    lan_controller_log_id: t("systemControls.general.lan_controller_log_id", { defaultValue: "Lan Controller Log ID" }),
    attention_message: t("systemControls.general.attention_message", { defaultValue: "Attention Message" }),
    attention_time: t("systemControls.general.attention_time", { defaultValue: "Attention Time" })
  }), [t]);

  // Columns
  const columns = useMemo(() => [
    { key: "locationName", content: headerBar ? translations.locationName : attention?.location_name },
    { key: "fk_lan_controllers", content: headerBar ? translations.fk_lan_controllers : attention?.fk_lan_controllers },
    { key: "lan_controller_log_id", content: headerBar ? translations.lan_controller_log_id : attention?.lan_controller_log_id },
    { key: "attention_message", content: headerBar ? translations.attention_message : attention?.message },
    { key: "attention_time", content: headerBar ? translations.attention_time : formattedTime },
  ], [attention, headerBar, translations, formattedTime]);

  if (!attention && !headerBar) return null;

  return (
    <div style={{ height: "35px", width: "650px", boxSizing: "border-box", zIndex: "99999", position: "relative" }}>
      <div style={{
        width: "100%",
        height: "100%",
        textAlign: "center",
        alignContent: "center",
        boxSizing: "border-box",
        padding: attentionColor ? "0px 10px" : "0px 10px 0px 20px",
        backgroundColor: colors.greyLightSecondary,
        transition: "filter 0.3s",
        color: colors.greyDarkPrimary,
        ...fonts.paragraph,
        ...fonts.fontSize4,
        ...borderRadius,
        zIndex: "99999",
        position: "relative",
        borderLeft: attentionColor ? "10px solid "+attentionColor : "none"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "22px 45px 45px 45px 290px 125px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          {selectable ? <CheckBox checkStyle={CheckStyles.transparent} isChecked={isChecked} onChange={onCheck}/> : <span/>}
          {columns.map((col) => (
            headerBar ? (
              <TooltipString
                key={col.key}
                tooltipString={translations[col.key as keyof typeof translations]}
                contentString={String(col.content)}
              />
            ) : (
            <p key={col.key} style={{...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
              {col.content}
            </p>
          )))}
          <span></span>
        </div>
      </div>
    </div>
  );
}

export default memo(ATTENTION_BAR);